/**
 * useClientOfferReviewShellActions
 *
 * Map/list interaction handlers (fly-to, focus/scroll-into-view/pulse,
 * fullscreen toggle, layout-step resize) for client-offer-review-shell.tsx —
 * extracted to keep that component's own cyclomatic complexity within the
 * lint budget. Mirrors the mockup reference's equivalent handlers.
 */
import type { MutableRefObject, RefObject } from 'react';
import type { MapInstance } from '@ooh/ui/components/map-view';
import type { ClientOfferReviewLayoutStep, ClientOfferReviewUnit } from '../../client-offer-review';
import type { ClientOfferReviewViewState } from './use-client-offer-review-view-state';

export interface ClientOfferReviewShellActionsParams {
  units: ClientOfferReviewUnit[];
  view: ClientOfferReviewViewState;
  mapInstanceRef: RefObject<MapInstance | null>;
  mapWrapRef: RefObject<HTMLDivElement | null>;
  rowRefs: MutableRefObject<Record<string, HTMLDivElement | null>>;
}

export function useClientOfferReviewShellActions({
  units,
  view,
  mapInstanceRef,
  mapWrapRef,
  rowRefs,
}: ClientOfferReviewShellActionsParams) {
  function flyToUnit(id: string, zoom = 13) {
    const unit = units.find((u) => u.id === id);
    if (unit?.lat != null && unit.lng != null && mapInstanceRef.current) {
      mapInstanceRef.current.flyTo({ center: [unit.lng, unit.lat], zoom, duration: 900 });
    }
    view.setActiveId(id);
  }

  function focusUnit(id: string) {
    const ix = view.rows.findIndex((u) => u.id === id);
    view.setActiveId(id);
    if (ix >= 0 && view.pageSize !== 'all') view.onPageChange(Math.floor(ix / view.pageSize) + 1);
    window.setTimeout(() => {
      const el = rowRefs.current[id];
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      view.setPulseId(id);
      window.setTimeout(() => view.setPulseId((prev) => (prev === id ? null : prev)), 1400);
    }, 60);
  }

  function toggleFullscreen() {
    const el = mapWrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) void el.requestFullscreen?.();
    else void document.exitFullscreen?.();
    window.setTimeout(() => mapInstanceRef.current?.resize(), 400);
  }

  function onLayoutStepChange(step: ClientOfferReviewLayoutStep) {
    view.setLayoutStep(step);
    window.setTimeout(() => mapInstanceRef.current?.resize(), 220);
  }

  return { flyToUnit, focusUnit, toggleFullscreen, onLayoutStepChange };
}
